import React from 'react';

interface DateSelectorProps {
  selectedDate: string;
  availableDates: string[];
  onSelect: (date: string) => void;
}

/**
 * DateSelector - A date selection component with navigation controls.
 *
 * Provides a user-friendly interface for selecting dates from a list of available dates.
 * The component displays the currently selected date and allows users to navigate through
 * available dates using previous/next buttons.
 *
 * @param selectedDate - Currently selected date in YYYY-MM-DD format
 * @param availableDates - Array of available dates in YYYY-MM-DD format
 * @param onSelect - Callback function when a date is selected
 *
 * @example
 * ```tsx
 * <DateSelector
 *   selectedDate="2025-01-15"
 *   availableDates={['2025-01-15', '2025-01-16', '2025-01-20']}
 *   onSelect={(date) => setSelectedDate(date)}
 * />
 * ```
 */
const DateSelector: React.FC<DateSelectorProps> = ({ selectedDate, availableDates, onSelect }) => {
  const idx = availableDates.indexOf(selectedDate);
  // Simple date formatting without timezone processing
  const [year, month, day] = selectedDate.split('-').map(Number);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  // Create date object for day of week calculation
  const dateObj = new Date(year, month - 1, day);
  const dayOfWeek = dayNames[dateObj.getDay()];
  const monthName = monthNames[month - 1];
  
  const selectedDateLabel = `${dayOfWeek}, ${monthName} ${day}, ${year}`;

  const prevDate = idx > 0 ? availableDates[idx - 1] : null;
  const nextDate = idx < availableDates.length - 1 ? availableDates[idx + 1] : null;

  const prevDateLabel = prevDate ? (() => {
    const [pYear, pMonth, pDay] = prevDate.split('-').map(Number);
    const pDateObj = new Date(pYear, pMonth - 1, pDay);
    const pDayOfWeek = dayNames[pDateObj.getDay()];
    const pMonthName = monthNames[pMonth - 1];
    return `${pDayOfWeek}, ${pMonthName} ${pDay}, ${pYear}`;
  })() : '';

  const nextDateLabel = nextDate ? (() => {
    const [nYear, nMonth, nDay] = nextDate.split('-').map(Number);
    const nDateObj = new Date(nYear, nMonth - 1, nDay);
    const nDayOfWeek = dayNames[nDateObj.getDay()];
    const nMonthName = monthNames[nMonth - 1];
    return `${nDayOfWeek}, ${nMonthName} ${nDay}, ${nYear}`;
  })() : '';

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }} role="group" aria-label="Date selection">
      <button
        style={{ background: 'none', border: 'none', fontSize: 24, color: '#185F93', cursor: 'pointer', fontWeight: 'bold', padding: '14px 0 0 18px', minWidth: 'auto' }}
        onClick={() => idx > 0 && onSelect(availableDates[idx - 1])}
        disabled={idx === 0}
        aria-label={idx === 0 ? 'No previous date available' : `Select previous date: ${prevDateLabel}`}
        aria-disabled={idx === 0}
      >←</button>
      <div style={{ textAlign: 'center', position: 'relative', padding: '0 4px', flex: 1, minWidth: 0 }}>
        <span style={{ 
          fontWeight: 600, 
          color: '#185F93', 
          fontSize: '1.1rem', 
          display: 'inline-block',
          position: 'relative'
        }} role="status" aria-live="polite">
          {selectedDateLabel}
          <div style={{ 
            position: 'absolute', 
            bottom: -2, 
            left: 0, 
            right: 0, 
            height: 2, 
            backgroundColor: '#e69e6d' 
          }}></div>
        </span>
      </div>
      <button
        style={{ background: 'none', border: 'none', fontSize: 24, color: '#185F93', cursor: 'pointer', fontWeight: 'bold', padding: '8px 10px 0 0', minWidth: 'auto' }}
        onClick={() => idx < availableDates.length - 1 && onSelect(availableDates[idx + 1])}
        disabled={idx === availableDates.length - 1}
        aria-label={idx === availableDates.length - 1 ? 'No next date available' : `Select next date: ${nextDateLabel}`}
        aria-disabled={idx === availableDates.length - 1}
      >→</button>
    </div>
  );
};

export default DateSelector; 